import { ReactNode } from "react";

import { css, cssMap } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { wacToken } from "@/libraries/design/foundations";

import { PaidLandingTypes } from "../src/paid-landing.template";

const heroStyles = cssMap({
    "jira-software": {
        "backgroundColor": "#DEEBFF",
        "backgroundImage": `linear-gradient(transparent, transparent), linear-gradient(287.15deg, #FAFBFC 0%, #DEEBFF 8.06%, #B3D4FF 35.45%, #DEEBFF 77.6%, #FAFBFC 100%)`,
        "backgroundPosition": "top right, 0 0",
        "backgroundRepeat": "no-repeat",
        "backgroundSize": "auto 100% , auto",

        "@media": {
            "(min-width: 1024px)": {
                backgroundImage: `
                    url("https://dam-cdn.atl.orangelogic.com/AssetLink/iekw5140qufbmub57w25475d1h842f6j.webp"),
                    linear-gradient(287.15deg, #FAFBFC 0%, #DEEBFF 8.06%, #B3D4FF 35.45%, #DEEBFF 77.6%, #FAFBFC 100%)`
            }
        }
    },
    "jira-software-paid-ai": {},
    "jira-software-anchor": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#000",
        // eslint-disable-next-line local/ensure-wac-token-usage, @compiled/shorthand-property-sorting
        backgroundColor: "#1868db",

        h1: {
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#fff",
            fontWeight: "bold"
        },

        h3: {
            fontWeight: "400"
        },

        h4: {
            textAlign: "center"
        }
    },
    "jira-software-rebranded-anchor": {
        "marginBlockStart": "60px",
        "position": "relative",
        "&:before": {
            content: '""',
            position: "absolute",
            insetBlockStart: "20px",
            insetInlineStart: 0,
            insetInlineEnd: "100px",
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#DEEBFF",
            insetBlockEnd: "70px",
            transform: "rotate(-1.25deg)",
            transformOrigin: "center",
            zIndex: -5,
            pointerEvents: "none"
        },
        "&:after": {
            content: '""',
            position: "absolute",
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#DEEBFF",
            insetBlockStart: "63px",
            insetInlineStart: "200px",
            insetInlineEnd: 0,
            insetBlockEnd: 0,
            transform: "rotate(1deg)",
            transformOrigin: "center",
            zIndex: -5,
            pointerEvents: "none"
        }
    },
    "jira-service-management": {
        hyphens: "auto", // GWP-1475 temporary fix. See more here: https://hello.atlassian.net/wiki/x/cwl0z
        // eslint-disable-next-line @compiled/shorthand-property-sorting
        background:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "linear-gradient(286.99deg, #064464 6.32%, #0049B0 42.11%, #6D5DB5 75.48%, #FF8F73 103.33%)"
    },
    "jira-service-management-anchor": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#000",
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#1868db",

        h1: {
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#fff",
            fontWeight: "bold"
        },

        h3: {
            fontWeight: "400"
        },

        h4: {
            textAlign: "center"
        }
    },
    "confluence-paid": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0 10px 14px rgba(0, 0, 0, 0.1)",
        backgroundImage: `url("https://dam-cdn.atl.orangelogic.com/AssetLink/173352i45eus05h3sgx080he00axu7vy.svg"), linear-gradient(180deg, #c9f2ff 0%, #e6fcff 96.63%)`,
        backgroundPosition: "center right, center",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "50% auto, cover"
    },
    "confluence-anchor": {
        "boxShadow": "0 10px 14px rgba(0, 0, 0, 0.1)",
        "backgroundImage": `linear-gradient(90deg, #C9F2FF 0%, #E7F9FF 98.64%)`,
        "backgroundPosition": "center",
        "backgroundRepeat": "no-repeat",
        "backgroundSize": "cover",

        "@media": {
            "(min-width: 1024px)": {
                backgroundImage: `linear-gradient(180deg, #C9F2FF99 0%, #E7F9FF99 96.63%), url("https://dam-cdn.atl.orangelogic.com/AssetLink/4isk02lkm8uedjkvrikq7ig1wk0b050a.svg"), url("https://dam-cdn.atl.orangelogic.com/AssetLink/5b57r223sdb02n0pb48y024to20fj25d.svg"), linear-gradient(180deg, #C9F2FF 0%, #E7F9FF 96.63%)`,
                backgroundPosition:
                    "center left, center left, center right, center right",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain"
            }
        }
    },
    "confluence-rebranded-anchor": {
        "marginBlockStart": "60px",
        "position": "relative",
        "&:before": {
            content: '""',
            position: "absolute",
            insetBlockStart: "20px",
            insetInlineStart: 0,
            insetInlineEnd: "100px",
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#DEEBFF",
            insetBlockEnd: "70px",
            transform: "rotate(-1.25deg)",
            transformOrigin: "center",
            zIndex: -5,
            pointerEvents: "none"
        },
        "&:after": {
            content: '""',
            position: "absolute",
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#DEEBFF",
            insetBlockStart: "63px",
            insetInlineStart: "200px",
            insetInlineEnd: 0,
            insetBlockEnd: 0,
            transform: "rotate(1deg)",
            transformOrigin: "center",
            zIndex: -5,
            pointerEvents: "none"
        }
    }
});

const JSMRebrandedAnchorBackground = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#0C66E4 !important"
});

const darkThemeEmailForm = css({
    // eslint-disable-next-line @compiled/shorthand-property-sorting
    backgroundColor: wacToken("color.primary.blue"),
    color: wacToken("color.neutral.light0"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".workEmail"]: {
        color: wacToken("color.neutral.light0")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".SignupSubmitBtn"]: {
        backgroundColor: wacToken("color.primary.saffron"),
        color: `${wacToken("color.primary.black")} !important`,
        [":hover"]: {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#FF8B00",
            color: `${wacToken("color.primary.black")} !important`
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".separatorText"]: {
        color: wacToken("color.neutral.light0")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".linkText"]: {
        color: `${wacToken("color.neutral.light0")} !important`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".errorMessageText"]: {
        color: `${wacToken("color.extended.saffron300")} !important`
    }
});

const defaultHeroStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#DEEBFF",
    backgroundImage: `linear-gradient(transparent, transparent), linear-gradient(287.15deg, #FAFBFC 0%, #DEEBFF 8.06%, #B3D4FF 35.45%, #DEEBFF 77.6%, #FAFBFC 100%)`,
    backgroundPosition: "top right, 0 0",
    backgroundRepeat: "no-repeat",
    backgroundSize: "auto 100% , auto",
    [media.above.lg]: {
        backgroundImage: `
                url("https://dam-cdn.atl.orangelogic.com/AssetLink/iekw5140qufbmub57w25475d1h842f6j.webp"),
                linear-gradient(287.15deg, #FAFBFC 0%, #DEEBFF 8.06%, #B3D4FF 35.45%, #DEEBFF 77.6%, #FAFBFC 100%)`
    }
});

const padding = css({ paddingBlockStart: "60px" });

interface PaidHeroProps {
    darkSolidTheme?: boolean;
    landingType: PaidLandingTypes;
    heroComponent: ReactNode;
    showAnimatedHero: boolean;
    showNewHero?: boolean;
}

export const PaidHeroSectionTypes = ({
    darkSolidTheme,
    landingType,
    heroComponent,
    showAnimatedHero,
    showNewHero
}: PaidHeroProps) => {
    const customHero =
        !darkSolidTheme && showNewHero && heroStyles[landingType];
    const defaultHero = !darkSolidTheme && !showNewHero && defaultHeroStyles;
    const isRebrandedJSMHero =
        darkSolidTheme &&
        showNewHero &&
        landingType === "jira-service-management-anchor";

    return (
        <section
            id="start-trial"
            css={[
                showAnimatedHero && darkSolidTheme && darkThemeEmailForm,
                showAnimatedHero && customHero,
                showAnimatedHero && defaultHero,
                !showAnimatedHero && heroStyles[landingType],
                isRebrandedJSMHero && JSMRebrandedAnchorBackground,
                padding
            ]}
        >
            {heroComponent}
        </section>
    );
};

PaidHeroSectionTypes.displayName = "PaidHero";
